import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Prices from "components/Prices";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import CommonLayout from "./CommonLayout";
import { fetchUserOrders } from "api";
import { baseUrl } from "api";
import { API_ENDPOINTS } from "api/apiEndPoints";

interface OrderItem {
  id?: string | number;
  item_id?: string | number;
  item_name?: string;
  qty?: number;
  rate?: number;
  net_amount?: number;
  image?: string;
  item_image?: string;
  attributes?: string;
  item_attributes?: string;
  can_review?: boolean;
}

interface Order {
  id?: string | number;
  uuid?: string;
  sales_order_no?: string;
  sales_order_date?: string;
  order_date?: string;
  approved?: string;
  order_status?: string;
  status?: string;
  payment_status?: string;
  sales_order_item?: OrderItem[];
  items?: OrderItem[];
}

const AccountOrder = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchUserOrders();
        
        if (response?.success && response?.data) {
          // Handle both array response and object with orders property
          const ordersData = Array.isArray(response.data) 
            ? response.data 
            : response.data.orders || response.data.list || [];
          setOrders(ordersData);
        } else if (Array.isArray(response)) {
          setOrders(response);
        } else {
          setError("No orders found");
        }
      } catch (err: any) {
        console.error("Error fetching orders:", err);
        setError(err?.response?.data?.message || "Failed to load orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getPaymentStatus = (order: Order): string => {
    // Prioritize payment_status, then fallback to order status if payment_status is not available
    return (
      order.payment_status ||
      order.order_status ||
      order.status ||
      order.approved ||
      "Pending"
    );
  };

  const getOrderId = (order: Order): string => {
    return order.sales_order_no || `#${order.uuid}` || `#${order.id}` || "N/A";
  };

  const getOrderDate = (order: Order): string => {
    return order.sales_order_date || order.order_date || "";
  };

  const handleViewOrder = (order: Order) => {
    if (order.uuid) {
      navigate(`/payment-success/${order.uuid}`);
    }
  };

  const getImageUrl = (imagePath?: string): string | null => {
    if (!imagePath || imagePath.trim() === "") return null;
    
    // If already absolute URL (http/https), return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    
    // If relative path, construct full URL
    // Handle baseUrl with/without trailing slash and path with/without leading slash
    const base = baseUrl?.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const path = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
    return `${base}${path}`;
  };

  const renderProductItem = (item: OrderItem, index: number) => {
    const itemImage = item.image || item.item_image;
    const itemName = item.item_name || "Product";
    const attributes = item.attributes || item.item_attributes || "Standard";
    const quantity = item.qty || 1;
    const price = item.net_amount || item.rate || 0;
    const canReview = item.can_review !== false; // Show review link by default if not explicitly false
    const imageUrl = getImageUrl(itemImage);

    return (
      <div key={item.id || item.item_id || index} className="flex py-4 sm:py-7 last:pb-0 first:pt-0">
        <div className="h-24 w-16 sm:w-20 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={itemName}
              className="h-full w-full object-cover object-center"
              onError={(e) => {
                // Fallback to placeholder if image fails to load
                (e.target as HTMLImageElement).style.display = "none";
                (e.target as HTMLImageElement).nextElementSibling?.classList.remove("hidden");
              }}
            />
          ) : null}
          <div className={`h-full w-full flex items-center justify-center bg-slate-200 text-slate-400 text-xs ${imageUrl ? "hidden" : ""}`}>
            No Image
          </div>
        </div>

        <div className="ml-4 flex flex-1 flex-col">
          <div>
            <div className="flex justify-between">
              <div>
                <h3 className="text-base font-medium line-clamp-1">{itemName}</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  <span>{attributes}</span>
                </p>
              </div>
              <Prices className="mt-0.5 ml-2" price={price} />
            </div>
          </div>
          <div className="flex flex-1 items-end justify-between text-sm">
            <p className="text-gray-500 dark:text-slate-400 flex items-center">
              <span className="hidden sm:inline-block">Qty</span>
              <span className="inline-block sm:hidden">x</span>
              <span className="ml-2">{quantity}</span>
            </p>

            <div className="flex">
              {canReview && (
                <button
                  type="button"
                  className="font-medium text-indigo-600 dark:text-primary-500 hover:underline"
                  onClick={() => {
                    // TODO: Implement review functionality
                    console.log("Leave review for item:", item);
                  }}
                >
                  Leave review
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderOrder = (order: Order, index: number) => {
    const orderId = getOrderId(order);
    const orderDate = formatDate(getOrderDate(order));
    const paymentStatus = getPaymentStatus(order);
    const orderItems = order.sales_order_item || order.items || [];

    // Format payment status for display
    const formatPaymentStatus = (status: string): string => {
      const lowerStatus = status.toLowerCase();
      if (lowerStatus === "success" || lowerStatus === "paid" || lowerStatus === "completed") {
        return "Success";
      } else if (lowerStatus === "failure" || lowerStatus === "failed") {
        return "Failure";
      } else if (lowerStatus === "pending" || lowerStatus === "processing") {
        return "Pending";
      }
      return status;
    };

    const displayStatus = formatPaymentStatus(paymentStatus);

    return (
      <div key={order.id || order.uuid || index} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden z-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 sm:p-8 bg-slate-50 dark:bg-slate-500/5">
          <div>
            <p className="text-lg font-semibold">Order No. {orderId}</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1.5 sm:mt-2">
              {orderDate && <span>{orderDate}</span>}
              {orderDate && displayStatus && <span className="mx-2">·</span>}
              {displayStatus && (
                <span className={`capitalize ${
                  displayStatus.toLowerCase() === "success" || displayStatus.toLowerCase() === "paid" 
                    ? "text-green-600" 
                    : displayStatus.toLowerCase() === "failure" || displayStatus.toLowerCase() === "failed"
                    ? "text-red-600"
                    : "text-primary-500"
                }`}>
                  {displayStatus}
                </span>
              )}
            </p>
          </div>
          <div className="mt-3 sm:mt-0">
            <ButtonSecondary
              sizeClass="py-2.5 px-4 sm:px-6"
              fontSize="text-sm font-medium"
              onClick={() => handleViewOrder(order)}
            >
              View Order
            </ButtonSecondary>
          </div>
        </div>
        {orderItems.length > 0 ? (
          <div className="border-t border-slate-200 dark:border-slate-700 p-2 sm:p-8 divide-y divide-y-slate-200 dark:divide-slate-700">
            {orderItems.map((item, itemIndex) => renderProductItem(item, itemIndex))}
          </div>
        ) : (
          <div className="border-t border-slate-200 dark:border-slate-700 p-8">
            <p className="text-slate-500 dark:text-slate-400 text-sm">No items found in this order</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div>
        <CommonLayout>
          <div className="space-y-10 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl font-semibold">Order History</h2>
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400">Loading orders...</p>
            </div>
          </div>
        </CommonLayout>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <CommonLayout>
          <div className="space-y-10 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl font-semibold">Order History</h2>
            <div className="text-center py-12">
              <p className="text-red-500 dark:text-red-400">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Retry
              </button>
            </div>
          </div>
        </CommonLayout>
      </div>
    );
  }

  return (
    <div>
      <CommonLayout>
        <div className="space-y-10 sm:space-y-12">
          {/* HEADING */}
          <h2 className="text-2xl sm:text-3xl font-semibold">Order History</h2>
          
          {orders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500 dark:text-slate-400">No orders found</p>
              <Link
                to="/"
                className="mt-4 inline-block px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                Start Shopping
              </Link>
            </div>
          ) : (
            orders.map((order, index) => renderOrder(order, index))
          )}
        </div>
      </CommonLayout>
    </div>
  );
};

export default AccountOrder;
